import { z } from 'zod';

import { foldDigitsToAscii } from '@shared/format';

/**
 * The title form — the same fields on authoring and on editing, because they
 * edit the same record.
 *
 * **What the record holds is not automatically what the form draws.** Two of the
 * universal fields are absent as controls and neither is dropped from the model:
 *
 * - **The owner** is written by construction — the operator who authors a title
 *   owns it, and no row gives anyone a control that assigns a title to another
 *   account. A read-only line restating an unchangeable value costs a row of the
 *   form and answers no question.
 * - **The price and the access mode** are both absent, and the catalog says so
 *   in one line: `entitlement_modes` permits subscription and manual grant only,
 *   so «the free and one-off-purchase rows of §11 have no surface». The console
 *   drew an access select anyway, and the owning service holds no `access_mode`
 *   at all — no column, no field, no route — so every save carried a value
 *   nothing could store and every one of them was refused for it.
 *
 * **`required` here means required before publication, not before saving.** A
 * save produces a draft and a draft is allowed to be incomplete; the service
 * validates `REQUIRED_METADATA` at the publish transition, which happens on the
 * content list. So the schema marks the fields and the surface states the
 * requirement — it does not gate the submit.
 *
 * **The schema validates; it does not convert.** The year stays a folded ASCII
 * string and `toTitleWriteBody` turns it into the integer the wire takes, so the
 * form's value type and its field type stay the same shape.
 */

export const titleFormSchema = z.object({
  // **The title is required here, not only at the service.** Without a rule the
  // client resolver had nothing to refuse, so an empty form made a real request,
  // came back `422`, and the console painted Pydantic's own English under an
  // Arabic label — «String should have at least 1 character». The rule is named by
  // code so the copy that renders it is Arabic and lives beside the other copy.
  title: z.string().trim().min(1, 'required'),
  description: z.string().trim(),
  categoryId: z.string(),
  /**
   * Chosen from the managed vocabulary, never typed.
   *
   * The service takes tag **identifiers**; the field used to be free text, so a
   * save either sent names the service refused or, had it accepted them, would
   * have split one listing across two spellings of the same tag. The hint
   * already said the vocabulary is managed on its own surface — the control now
   * says it too.
   */
  tagIds: z.array(z.string()),
  productionYear: z
    .string()
    .trim()
    // An operator on an Arabic keyboard types `٢٠٢١`; rejecting that rejects the
    // glyph their keyboard produced rather than the number they meant.
    .transform((value) => foldDigitsToAscii(value))
    .pipe(z.string().regex(/^\d{0,4}$/, 'yearShape')),
  producingCountry: z.string().trim(),
  ageRating: z.string(),
});

export type TitleFormValues = z.infer<typeof titleFormSchema>;

export const EMPTY_TITLE_FORM: TitleFormValues = {
  title: '',
  description: '',
  categoryId: '',
  tagIds: [],
  productionYear: '',
  producingCountry: '',
  ageRating: '',
};

/**
 * The request body.
 *
 * Empty strings become `null` rather than travelling as `""`: a draft with no
 * production year has no production year, and an empty string is a value the
 * type's own schema would then have to reject.
 */
/**
 * The year as an integer, or `null` when the field is empty.
 *
 * Arabic-Indic digits are folded first, so the glyph the operator's keyboard
 * produced is not the reason their year disappears. A value that is not a year
 * after folding stays `null` and the form's own validation is what names it —
 * this function never invents a number.
 */
function toYear(raw: string): number | null {
  const folded = foldDigitsToAscii(raw).trim();
  if (folded === '') return null;
  return /^\d{1,4}$/.test(folded) ? Number(folded) : null;
}

export function toTitleWriteBody(values: TitleFormValues) {
  const nullable = (value: string) => (value.trim() === '' ? null : value.trim());

  return {
    title: values.title,
    description: values.description,
    // Both are **lists** on the wire, and the form holds one category because
    // that is the choice the surface offers — not because the record holds one.
    categoryIds: values.categoryId.trim() === '' ? [] : [values.categoryId.trim()],
    tagIds: values.tagIds,
    // **`metadata` is data inside a JSON column, so its keys are the registry's
    // own spelling** — snake_case — and no request-model alias reaches inside it.
    // The country is a two-letter ISO code and the service compares it upper-case;
    // folding here means an operator who typed `iq` is not refused for the shift key.
    metadata: {
      // **Folded here, not only in the schema's transform.** This builder is called
      // with `form.getValues()`, which hands back the *raw* input — the transform on
      // the field runs during validation and its output never reaches this function.
      // So a year typed «٢٠٢٤» arrived as `Number('٢٠٢٤')` → `NaN` →
      // `JSON.stringify` → `null`: the operator saw a filled field, the checklist
      // counted it complete, and the record was stored without a year, only to be
      // refused at publish for missing metadata that the form said was present.
      production_year: toYear(values.productionYear),
      producing_country:
        values.producingCountry.trim() === '' ? null : values.producingCountry.trim().toUpperCase(),
      age_rating: nullable(values.ageRating),
    },
  };
}

/**
 * Which of the required fields are still outstanding.
 *
 * **It is a reading, not a gate.** The list beside the form says what remains
 * before the title can be published; it carries no button and stops nothing,
 * because saving produces a draft and the requirement binds publication.
 *
 * The membership is read from `REQUIRED_METADATA` rather than compiled in, so a
 * change to the setting reaches this list without a release. The cover is the
 * one member that is not a form field — it is an upload — so it is passed in.
 */
export function outstandingRequirements(
  requiredMetadata: readonly string[],
  values: TitleFormValues,
  hasCover: boolean,
): string[] {
  const present: Readonly<Record<string, boolean>> = {
    title: values.title.trim() !== '',
    description: values.description.trim() !== '',
    genre: values.categoryId !== '',
    cover: hasCover,
    production_year: values.productionYear.trim() !== '',
    producing_country: values.producingCountry.trim() !== '',
    age_rating: values.ageRating !== '',
  };

  return requiredMetadata.filter((member) => present[member] === false);
}
